import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";

import useTheme from "hooks/useTheme";
import useAssetTransactions from "hooks/assets/useAssetTransactions";
import useNotification from "hooks/useNotification";

import TransactionPayDetailModal from "theme/modals/transactions/TransactionPayDetailModal";
import TransactionAssetTransferDetailModal from "theme/modals/transactions/TransactionAssetTransferDetailModal";
import TransactionAppCallDetailModal from "theme/modals/transactions/TransactionAppCallDetailModal";

import ASARenownedIcon from "theme/icons/ASARenowned";
import ASANotableIcon from "theme/icons/ASANotable";
import ASAVerifiedStandardIcon from "theme/icons/ASAVerifiedStandard";
import ASAFlaggedIcon from "theme/icons/ASAFlagged";
import ASANotVerifiedIcon from "theme/icons/ASANotVerified";
import CoinReceivedIcon from "theme/icons/CoinsReceived";
import CoinSentIcon from "theme/icons/CoinsSent";
import { Transition } from "@headlessui/react";
import TransactionsLoader from "theme/loaders/dashboard/TransactionsLoader";
import OptInAsaIcon from "theme/icons/OptInASA";
import CopyIcon from "theme/icons/Copy";
import SendIcon from "theme/icons/Send";
import AlammexIcon from "theme/icons/Alammex";
import ArrowBackIcon from "theme/icons/ArrowBack";
import AlgoExplorerIcon from "theme/icons/AlgoExplorer";
import VestigeIcon from "theme/icons/Vestige";
import useRefresh from "hooks/useRefresh";
import StyledTooltipWithoutArrow from "theme/tooltips/StyledTooltipWithoutArrow";

const AssetInfo = ({ navigatePage }) => {
	const location = useLocation();
	const { appLanguage, themeColor } = useTheme();
	const observer = useRef();
	const { notify } = useNotification();
	const { isRefresh } = useRefresh();

	const accounts = useSelector((state) => state.Accounts);
	const contacts = useSelector((state) => state.Contacts);
	const accountInfo = useSelector((state) => state.AccountInfo);
	const algoChain = useSelector((state) => state.AppPreferences.algoChain);

	const [assetInfo, setAssetInfo] = useState(null);
	const [pageNum, setPageNum] = useState(1);

	const [transactionPayDetailModalIsOpen, setTransactionPayDetailModalIsOpen] = useState(false);
	const [transactionPayDetailModalInfo, setTransactionPayDetailModalInfo] = useState(null);

	const [transactionAssetTransferDetailModalIsOpen, setTransactionAssetTransferDetailModalIsOpen] = useState(false);
	const [transactionAssetTransferDetailModalInfo, setTransactionAssetTransferDetailModalInfo] = useState(null);

	const [transactionAppCallDetailModalIsOpen, setTransactionAppCallDetailModalIsOpen] = useState(false);
	const [transactionAppCallDetailModalInfo, setTransactionAppCallDetailModalInfo] = useState(null);

	useEffect(() => {
		setAssetInfo(null);
		setPageNum(1);
	}, []);

	useEffect(() => {
		if (location?.state?.assetInfo) setAssetInfo(location.state.assetInfo);
	}, [location]);

	const { isLoading, error, transactions, hasMore, refreshTransactions } = useAssetTransactions(
		assetInfo?.["asset-id"],
		accountInfo?.address,
		pageNum,
		10,
		algoChain
	);

	const lastBookElementRef = useCallback(
		(node) => {
			if (isLoading) return;
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasMore) {
					setPageNum((prev) => prev + 1);
				}
			});
			if (node) observer.current.observe(node);
		},
		[isLoading, hasMore]
	);

	const getAccountName = (accountAddress) => {
		const contact = contacts.find((contact) => contact?.["account-address"] === accountAddress);
		const account = accounts.find((account) => account?.["account-address"] === accountAddress);

		if (contact?.["friendly-name"]) return contact["friendly-name"];
		else if (account?.name) return account.name;
		else return accountAddress.slice(0, 5) + "..." + accountAddress.slice(-5);
	};

	useEffect(() => {
		if (isRefresh)
			if (isRefresh) {
				setPageNum(1);
				refreshTransactions();
			}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRefresh]);

	return (
		<Transition
			as={Fragment}
			appear={true}
			show={true}
			enter="transition-opacity duration-500"
			enterFrom="opacity-0"
			enterTo="opacity-100"
		>
			<div className="flex flex-col w-full items-center justify-center relative pb-5">
				<StyledTooltipWithoutArrow
					title={appLanguage?.goBack}
					children={
						<ArrowBackIcon
							className={`absolute left-20 top-0 w-4 cursor-pointer`}
							fill={themeColor?.hex}
							onClick={() => navigatePage(-1)}
						/>
					}
				/>

				{/* Asset Details Section */}
				<div className="flex flex-col w-[50%] items-center justify-center">
					<div className="flex w-full items-center justify-between">
						<p className={`text-md font-medium ${themeColor?.text}`}>{appLanguage?.name}</p>
						<div
							className="flex w-full items-center justify-end cursor-pointer space-x-2"
							onClick={() =>
								window.open(
									assetInfo?.["asset-id"] === -1
										? "https://vestige.fi/algorand"
										: `https://vestige.fi/asset/${assetInfo?.["asset-id"]}`,
									"_blank"
								)
							}
						>
							<div className="flex flex-col items-center justify-center">
								{assetInfo?.verification ? (
									assetInfo?.verification?.reputation === "Verified" ? (
										<ASARenownedIcon className="w-3" fill={themeColor?.hex} />
									) : assetInfo?.verification?.reputation === "Notable" ? (
										<ASANotableIcon className="w-3" fill={themeColor?.hex} />
									) : assetInfo?.verification?.reputation === "Neutral" ? (
										<ASAVerifiedStandardIcon className="w-3" fill={themeColor?.hex} />
									) : assetInfo?.verification?.reputation === "Unsafe" ? (
										<ASAFlaggedIcon className="w-3" fill={themeColor?.hex} />
									) : assetInfo?.verification?.reputation === "Not Verified" ? (
										<ASANotVerifiedIcon className="w-3" fill={themeColor?.hex} />
									) : (
										assetInfo?.verification?.reputation === "" && (
											<ASANotVerifiedIcon className="w-3" fill={themeColor?.hex} />
										)
									)
								) : (
									<ASANotVerifiedIcon className="w-3" fill={themeColor?.hex} />
								)}

								<ASANotVerifiedIcon className="w-3" fill={themeColor?.hex} />
							</div>
							<p className={`text-md font-medium ${themeColor?.text}`}>{assetInfo?.["unit-name"]}</p>
							<img
								className="w-5"
								alt="asset"
								src={`http://localhost:5020/v1/assets/image?assetID=${assetInfo?.["asset-id"]}&width=60`}
							/>
						</div>
					</div>

					<div className="flex w-full items-center justify-between">
						<h1 className={`text-md font-medium ${themeColor?.text}`}>{appLanguage?.balance}</h1>
						<h1 className={`text-md font-medium ${themeColor?.text}`}>
							{assetInfo?.amount &&
								(
									assetInfo.amount / (assetInfo["asset-id"] === -1 ? 1000000 : Math.pow(10, assetInfo.decimals))
								).toLocaleString(undefined, {
									maximumFractionDigits: assetInfo.decimals,
								})}
						</h1>
					</div>

					<div className="flex w-full items-center justify-between">
						<h1 className={`text-md font-medium ${themeColor?.text}`}>{appLanguage?.id}</h1>
						<div className="flex w-full items-center justify-end space-x-2">
							<VestigeIcon
								className={`w-3 cursor-pointer`}
								fill={themeColor?.hex}
								onClick={() =>
									window.open(
										assetInfo["asset-id"] === 0
											? "https://vestige.fi/algorand"
											: `https://vestige.fi/asset/${assetInfo["asset-id"]}`,
										"_blank"
									)
								}
							/>
							<AlgoExplorerIcon
								className={`w-3 cursor-pointer`}
								fill={themeColor?.hex}
								onClick={() =>
									window.open(
										assetInfo["asset-id"] === 0
											? "https://algoexplorer.io"
											: `https://algoexplorer.io/asset/${assetInfo["asset-id"]}`,
										"_blank"
									)
								}
							/>
							<CopyIcon
								className={`w-4 cursor-pointer hover:shadow-lg`}
								fill={themeColor?.hex}
								onClick={() => {
									navigator.clipboard.writeText(assetInfo?.["asset-id"]);
									notify("success", "Copied to clipboard");
								}}
							/>
							<h1 className={`text-md font-medium ${themeColor?.text}`}>{assetInfo?.["asset-id"]}</h1>
						</div>
					</div>
				</div>

				{/* Asset transaction history and Send swap buttons */}
				<div className="flex w-[50%] items-center justify-start mt-4">
					<p className={`text-md font-medium ${themeColor?.text}`}>{appLanguage?.transactionHistory}</p>
				</div>
				<div className="flex-1 w-[50%] h-full overflow-y-auto">
					<div className="flex flex-col w-full space-y-2 pr-3 py-1">
						{transactions?.map((assetTransaction, index) => (
							<div
								state={{ transactionID: assetTransaction?.id }}
								className="flex w-full cursor-pointer"
								key={index}
								ref={lastBookElementRef}
							>
								{assetTransaction["tx-type"] === "pay" ? (
									assetTransaction["payment-transaction"]["receiver"] === assetTransaction.sender ||
									assetTransaction.sender !== accountInfo.address ? (
										<div
											className="flex w-full items-center justify-between"
											onClick={() => {
												setTransactionPayDetailModalInfo(assetTransaction);
												setTransactionPayDetailModalIsOpen(true);
											}}
										>
											<div className="flex items-start space-x-8">
												<CoinReceivedIcon className={`w-6`} fill={themeColor?.hex} />
												<div className="flex-col">
													<p className={`text-sm font-bold ${themeColor?.text}`}>
														{appLanguage?.received}
													</p>
													<p className={`text-sm font-thin ${themeColor?.text}`}>
														{assetTransaction?.["payment-transaction"]?.receiver &&
															getAccountName(assetTransaction?.sender)}
													</p>
												</div>
											</div>

											<div className="flex space-x-3 items-start">
												<div className=" flex flex-col w-fit items-end justify-end">
													<p className={`text-sm font-bold ${themeColor?.text}`}>
														{(
															assetTransaction["payment-transaction"]["amount"] / 1000000
														).toLocaleString(undefined, {
															maximumFractionDigits: 6,
														})}{" "}
														Algo
													</p>
													{/* <p className={`text-sm font-thin ${themeColor?.text}`}>0$</p> */}
												</div>
											</div>
										</div>
									) : (
										<div
											className="flex w-full items-center justify-between"
											onClick={() => {
												setTransactionPayDetailModalInfo(assetTransaction);
												setTransactionPayDetailModalIsOpen(true);
											}}
										>
											<div className="flex items-start space-x-8">
												<CoinSentIcon className={`w-6`} fill="#FC4242" />
												<div className="flex-col">
													<p className={`text-sm font-bold text-candy-red`}>{appLanguage?.sent}</p>
													<p className={`text-sm font-thin ${themeColor?.text}`}>
														{assetTransaction?.["payment-transaction"]?.receiver &&
															getAccountName(assetTransaction["payment-transaction"]["receiver"])}
													</p>
												</div>
											</div>

											<div className="flex space-x-3 items-start">
												<div className=" flex flex-col w-fit items-end justify-end">
													<p className={`text-sm font-bold text-candy-red`}>
														-
														{(
															assetTransaction["payment-transaction"]["amount"] / 1000000
														).toLocaleString(undefined, {
															maximumFractionDigits: 6,
														})}{" "}
														Algo
													</p>
													{/* <p className={`text-sm font-thin ${themeColor?.text}`}>0$</p> */}
												</div>
											</div>
										</div>
									)
								) : assetTransaction["tx-type"] === "axfer" ? (
									assetTransaction["asset-transfer-transaction"]["receiver"] === assetTransaction.sender ||
									assetTransaction.sender !== accountInfo.address ? (
										<div
											className="flex w-full items-center justify-between"
											onClick={() => {
												setTransactionAssetTransferDetailModalInfo(assetTransaction);
												setTransactionAssetTransferDetailModalIsOpen(true);
											}}
										>
											<div className="flex items-start space-x-8">
												<CoinReceivedIcon className={`w-6`} fill={themeColor?.hex} />
												<div className="flex-col">
													<p className={`text-sm font-bold ${themeColor?.text}`}>
														{appLanguage?.received}
													</p>
													<p className={`text-sm font-thin ${themeColor?.text}`}>
														{assetTransaction?.["asset-transfer-transaction"]?.receiver &&
															getAccountName(assetTransaction?.sender)}
													</p>
												</div>
											</div>

											<div className="flex space-x-3 items-start">
												<div className=" flex flex-col w-fit items-end justify-end">
													<p className={`text-sm font-bold ${themeColor?.text}`}>
														{(
															assetTransaction["asset-transfer-transaction"]["amount"] /
															Math.pow(
																10,
																assetTransaction["asset-transfer-transaction"]["asset-decimals"]
															)
														).toLocaleString(undefined, {
															maximumFractionDigits:
																assetTransaction["asset-transfer-transaction"]["asset-decimals"],
														})}{" "}
														{assetTransaction["asset-transfer-transaction"]["asset-unit-name"]}
													</p>
													{/* <p className={`text-sm font-thin ${themeColor?.text}`}>0$</p> */}
												</div>
											</div>
										</div>
									) : (
										<div
											className="flex w-full items-center justify-between"
											onClick={() => {
												setTransactionAssetTransferDetailModalInfo(assetTransaction);
												setTransactionAssetTransferDetailModalIsOpen(true);
											}}
										>
											<div className="flex items-start space-x-8">
												<CoinSentIcon className={`w-6`} fill="#FC4242" />
												<div className="flex-col">
													<p className={`text-sm font-bold text-candy-red`}>{appLanguage?.sent}</p>
													<p className={`text-sm font-thin ${themeColor?.text}`}>
														{assetTransaction?.["asset-transfer-transaction"]?.receiver &&
															getAccountName(
																assetTransaction["asset-transfer-transaction"]["receiver"]
															)}
													</p>
												</div>
											</div>

											<div className="flex space-x-3 items-start">
												<div className=" flex flex-col w-fit items-end justify-end">
													<p className={`text-sm font-bold text-candy-red`}>
														-
														{(
															assetTransaction["asset-transfer-transaction"]["amount"] /
															Math.pow(
																10,
																assetTransaction["asset-transfer-transaction"]["asset-decimals"]
															)
														).toLocaleString(undefined, {
															maximumFractionDigits:
																assetTransaction["asset-transfer-transaction"]["asset-decimals"],
														})}{" "}
														{assetTransaction["asset-transfer-transaction"]["asset-unit-name"]}
													</p>
													{/* <p className={`text-sm font-thin ${themeColor?.text}`}>0$</p> */}
												</div>
											</div>
										</div>
									)
								) : (
									assetTransaction["tx-type"] === "appl" && (
										<div
											className="flex w-full items-center justify-between"
											onClick={() => {
												setTransactionAppCallDetailModalInfo(assetTransaction);
												setTransactionAppCallDetailModalIsOpen(true);
											}}
										>
											<div className="flex items-start space-x-8">
												<OptInAsaIcon className={`w-6`} fill={themeColor?.hex} />
												<div className="flex-col">
													<p className={`text-sm font-bold ${themeColor?.text}`}>
														{appLanguage?.appCall}
													</p>
													<p className={`text-sm font-thin ${themeColor?.text}`}>
														{assetTransaction?.sender && getAccountName(assetTransaction?.sender)}
													</p>
												</div>
											</div>

											<div className="flex space-x-3 items-start">
												<div className=" flex flex-col w-fit items-end justify-end">
													<p className={`text-sm font-bold ${themeColor?.text}`}>
														{assetTransaction["inner-txns"] &&
															assetTransaction["inner-txns"].length + " inner txns"}
													</p>
													{/* <p className={`text-sm font-thin ${themeColor?.text}`}>-</p> */}
												</div>
											</div>
										</div>
									)
								)}
							</div>
						))}
						{isLoading && <TransactionsLoader />}
						{error && "Error..."}
					</div>
				</div>

				<div className={`flex absolute bottom-5 right-[30%] space-x-2`}>
					<StyledTooltipWithoutArrow
						title={appLanguage?.convert}
						children={
							<Link
								className={`px-3 py-3 rounded-full top-0 right-0 hover:shadow-lg ${themeColor?.bg}`}
								to="/Swap"
								state={{ asset: assetInfo }}
							>
								<AlammexIcon className="w-5" fill="#FFFFFF" />
							</Link>
						}
					/>
					<StyledTooltipWithoutArrow
						title={appLanguage?.send}
						children={
							<Link
								className={`px-3 py-3 rounded-full top-0 right-0 hover:shadow-lg ${themeColor?.bg}`}
								to="/Send"
								state={{ asset: assetInfo }}
							>
								<SendIcon className="w-5" fill="#FFFFFF" />
							</Link>
						}
					/>
				</div>

				<TransactionPayDetailModal
					isOpen={transactionPayDetailModalIsOpen}
					setIsOpen={setTransactionPayDetailModalIsOpen}
					modalInfo={transactionPayDetailModalInfo}
				/>
				<TransactionAssetTransferDetailModal
					isOpen={transactionAssetTransferDetailModalIsOpen}
					setIsOpen={setTransactionAssetTransferDetailModalIsOpen}
					modalInfo={transactionAssetTransferDetailModalInfo}
				/>
				<TransactionAppCallDetailModal
					isOpen={transactionAppCallDetailModalIsOpen}
					setIsOpen={setTransactionAppCallDetailModalIsOpen}
					modalInfo={transactionAppCallDetailModalInfo}
				/>
			</div>
		</Transition>
	);
};

export default AssetInfo;
